<template>
    <div :class="['notes-build', { 'notes-build--refreshed': refreshed }]">
        <div class="notes-build__label" v-text="$t(labelText)" />
        <Spinner v-if="false" class="notes-build__spinner" />
        <div class="notes-build__toolbar">
            <button
                :class="[
                    'button notes-build__button notes-build__button--download',
                    { 'is-disabled': downloadDisabled, 'is-loading': loading }
                ]"
                :disabled="downloadDisabled"
                @click="download"
            >
                {{ $t(loading ? 'misc.loading' : downloadText) }}
            </button>
            <button
                :class="[
                    'button button--outlined notes-build__button notes-build__button--create',
                    { 'is-disabled': loading, 'is-loading': loading }
                ]"
                :disabled="loading"
                @click="rebuild"
            >
                ↻ {{ $t(loading ? 'misc.loading' : createText) }}
            </button>
        </div>
        <div
            v-if="status"
            class="notes-build__status"
            v-text="$t(`notes.build.status.${status}`)"
        />
        <div v-if="!loading && !status && createTrieds > 0" class="notes-build__time">
            ⌚ {{ $t('notes.build.time') }} {{ buildTime }}
        </div>

        <a v-if="buildUrl" v-show="false" ref="link" :href="buildUrl" target="_blank" />
    </div>
</template>

<script>
import { noteBuilds } from '../../api/notes';

export default {
    name: 'NotesBuild',
    inject: ['registerWsHandler', 'removeWsHandler'],
    props: {
        bookId: {
            required: true,
            type: Number
        },
        buildType: {
            required: true,
            type: String
        },
        fetchInterval: {
            type: Number,
            default: 5000
        },
        fetchLimit: {
            type: Number,
            default: 5
        },
        labelText: {
            type: String,
            default: 'notes.build.label'
        },
        downloadText: {
            type: String,
            default: 'notes.build.download'
        },
        createText: {
            type: String,
            default: 'notes.build.create'
        },
        completedText: {
            type: String,
            default: 'notes.build.completed'
        }
    },
    data() {
        return {
            loading: false,
            refreshed: false,
            error: null,
            interval: 0,
            fetchTrieds: 0,
            createTrieds: 0,
            buildList: {},
            status: null
        };
    },
    computed: {
        downloadDisabled() {
            return this.loading || this.status === 'building' || !this.buildUrl;
        },

        latestBuild() {
            const items = Object.values(this.buildList).sort(
                (a, b) => new Date(b.modified) - new Date(a.modified)
            );
            return items[0] || {};
        },

        buildUrl() {
            return this.latestBuild.document || null;
        },

        buildTime() {
            const dt = new Date(this.latestBuild.modified);
            return dt.toLocaleTimeString(navigator.language, {
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    },
    watch: {
        buildUrl(url) {
            if (!url) return;

            this.loading = false;
            this.status = null;
            this.stopFetchInterval();
        },

        fetchTrieds(trieds) {
            if (trieds >= this.fetchLimit) this.stopFetchInterval('limited');
        },

        createTrieds() {
            this.refreshed = false;
        },

        'latestBuild.modified'(newVal, oldVal) {
            this.refreshed = new Date(newVal) > new Date(oldVal);
        }
    },
    async created() {
        this.registerWsHandler('complete_build_note', this.onCompleteBuild);
    },
    beforeUnmount() {
        this.removeWsHandler('complete_build_note', this.onCompleteBuild);
        this.stopFetchInterval();
    },
    methods: {
        async rebuild() {
            this.status = 'building';
            this.createTrieds = 0;

            this.stopFetchInterval();
            await this.createBuild();
            this.startFetchInterval();
        },

        startFetchInterval(immediately = true) {
            if (immediately) this.fetchBuilds();
            this.interval = setInterval(this.fetchBuilds, this.fetchInterval);
        },

        stopFetchInterval(msg) {
            clearInterval(this.interval);
            if (msg !== undefined) console.log(`[NotesBuild/stopFetchInterval]: ${msg}`);
        },

        async createBuild() {
            try {
                const { bookId: book, buildType: build_type } = this;
                await noteBuilds({ book, build_type });
                this.createTrieds++;
            } catch (e) {
                console.log(e);
            }
        },

        async fetchBuilds() {
            try {
                const { bookId: book, buildType: build_type } = this;
                const params = { book, build_type };
                const { count, results } = await this.$http.get('/api/v1/note_builds/', { params });

                this.fetchTrieds++;
                if (count) {
                    this.buildList = {
                        ...this.buildList,
                        ...results.reduce(
                            (acc, cur) => ({
                                ...acc,
                                [cur.id]: cur
                            }),
                            {}
                        )
                    };
                }
            } catch (e) {
                console.log(e);
            }
        },

        onCompleteBuild({ code, params, errors }) {
            if (code === 200) {
                if (params.build_type === this.buildType) {
                    this.status = null;
                    this.loading = false;
                    this.buildList[params.id] = { ...params, modified: Date.now() };
                    this.stopFetchInterval('builded');
                    return;
                }
            } else {
                if (errors) {
                    this.error = [{ detail: this.$t('notes.build.error') }];
                }
            }
        },
        download() {
            if (!this.buildUrl) return;
            this.$refs.link.click();
        }
    }
};
</script>

<style src="./NotesBuild.scss" lang="scss"></style>
